import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createContext, useState, useContext, useEffect, useCallback } from "react";
import { AuthContext } from "./AuthContext";
import i18n from "../i18n.js";

export const THEMES = {
  dark: {
    low:    { label: "Sombre — Doux",       bg: "#1a1a1f", text: "#a8a8b8", headerBg: "#0d1117", accent: "#1a237e" },
    medium: { label: "Sombre — Standard",   bg: "#111118", text: "#c0c0d8", headerBg: "#0d1117", accent: "#1a237e" },
    high:   { label: "Sombre — Contrasté",  bg: "#0a0a0f", text: "#d8d8f0", headerBg: "#0d1117", accent: "#283593" },
  },
  light: {
    low:    { label: "Clair — Très doux",   bg: "#f0ede8", text: "#555050", headerBg: "#3d3835", accent: "#3d3835" },
    medium: { label: "Clair — Standard",    bg: "#f5f3f0", text: "#444040", headerBg: "#302c2a", accent: "#302c2a" },
    high:   { label: "Clair — Contrasté",   bg: "#fafaf8", text: "#2a2828", headerBg: "#252222", accent: "#252222" },
  },
};

const getTheme = (mode, appTheme) =>
  createTheme({
    palette: {
      mode,
      background: { default: appTheme.bg },
    },
    custom: {
      mymodal: {
        main:   mode === "dark" ? "rgba(20,24,30,0.95)" : "#ffffff",
        text:   appTheme.text,
        button: appTheme.accent,
        border: mode === "dark" ? "1px solid #2f3545" : "1px solid #d3d7df",
      },
      myheader: {
        main: appTheme.headerBg,
        text: "#ffffff",
      },
    },
  });

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const { state: user } = useContext(AuthContext);

  const [mode, setMode] = useState(
    () => localStorage.getItem("theme-mode") || "dark"
  );
  const [intensity, setIntensity] = useState(
    () => localStorage.getItem("theme-intensity") || "medium"
  );

  const appTheme = THEMES[mode][intensity];

  // Charge les préférences dès que l'utilisateur est connecté
  useEffect(() => {
    if (!user) return;
    fetch("/api/preferences", { credentials: "include" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        if (data.pref_theme_mode) {
          setMode(data.pref_theme_mode);
          localStorage.setItem("theme-mode", data.pref_theme_mode);
        }
        if (data.pref_theme_intensity) {
          setIntensity(data.pref_theme_intensity);
          localStorage.setItem("theme-intensity", data.pref_theme_intensity);
        }
        if (data.pref_language) {
          i18n.changeLanguage(data.pref_language);
        }
      })
      .catch(() => {});
  }, [user]);

  const savePreferences = useCallback((updates) => {
    fetch("/api/preferences", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(updates),
    }).catch(() => {});
  }, []);

  const changeMode = (newMode) => {
    setMode(newMode);
    localStorage.setItem("theme-mode", newMode);
    savePreferences({ pref_theme_mode: newMode });
  };

  const changeIntensity = (newIntensity) => {
    setIntensity(newIntensity);
    localStorage.setItem("theme-intensity", newIntensity);
    savePreferences({ pref_theme_intensity: newIntensity });
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    savePreferences({ pref_language: lang });
  };

  return (
    <ThemeContext.Provider value={{ mode, intensity, appTheme, changeMode, changeIntensity, changeLanguage }}>
      <MuiThemeProvider theme={getTheme(mode, appTheme)}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeContext);
}
